import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { NT_IC, agoText, dmy, evVisible, reminderId, tkLate, today } from '@aop/domain';
import { useMe } from '../auth/AuthContext';
import { coAllowed, coLabel, getCompany, useCompanies, useSwitchCompany } from '../lib/company';
import { useRows } from '../lib/data';
import { supabase } from '../lib/supabase';
import { useEvents } from '../modules/cal/calData';
import { useTasks } from '../modules/tasks/tasksData';
import { Dialog } from '../ui/Dialog';
import { useToast } from '../ui/Toast';

export interface Notif { id: string; toUser: string; taskId: string | null; eventId: string | null; title: string; kind: string; text: string; byName: string; byId: string | null; at: string; read: boolean; companyId?: string | null }

/** Thông báo của tôi, mới nhất trước (CSDL chỉ trả thông báo gửi cho mình). */
export function useNotifications() {
  const me = useMe();
  const q = useRows<Notif>('notifications', { order: 'at', ascending: false });
  const mine = useMemo(() => (q.data ?? []).filter(n => n.toUser === me.id).sort((a, b) => (b.at || '').localeCompare(a.at || '')), [q.data, me.id]);
  return { ...q, mine, unread: mine.filter(n => !n.read) };
}

/**
 * Việc chạy nền của thanh bên: tiêu đề tab kèm số chưa đọc, toast khi có thông báo mới, và nhắc hạn
 * (mỗi người tự tạo nhắc cho việc mình phụ trách và lịch mình tham dự, mỗi mục tối đa một lần/ngày).
 */
export function useNotificationEffects() {
  const me = useMe();
  const toast = useToast();
  const qc = useQueryClient();
  const { mine, unread, isLoading } = useNotifications();
  const tasksQ = useTasks();
  const eventsQ = useEvents();
  const seen = useRef<Set<string> | null>(null);
  const remindedFor = useRef('');

  useEffect(() => {
    try { document.title = (unread.length ? `(${unread.length}) ` : '') + document.title.replace(/^\(\d+\+?\) /, ''); } catch { /* bỏ qua */ }
  }, [unread.length]);

  useEffect(() => {
    if (isLoading) return;
    if (seen.current) {
      const fresh = unread.filter(n => !seen.current!.has(n.id));
      if (fresh.length === 1) toast(`🔔 ${fresh[0].byName ? fresh[0].byName + ': ' : ''}${fresh[0].text}`);
      else if (fresh.length > 1) toast(`🔔 Bạn có ${fresh.length} thông báo mới`);
    }
    seen.current = new Set(mine.map(n => n.id));
  }, [mine, unread, isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoading || tasksQ.isLoading || eventsQ.isLoading) return;
    const d = today();
    if (remindedFor.current === d + me.id) return;
    remindedFor.current = d + me.id;
    const have = new Set(mine.map(n => n.id));
    const rows: Record<string, unknown>[] = [];
    for (const t of (tasksQ.data ?? []).filter(x => x.assigneeId === me.id && x.due && !['done', 'cancel'].includes(x.status))) {
      const late = tkLate(t, d), soon = !late && (+new Date(t.due) - +new Date(d)) / 864e5 <= 1;
      if (!late && !soon) continue;
      const id = reminderId('due', t.id, d, me.id);
      if (!have.has(id)) rows.push({ id, to_user: me.id, task_id: t.id, title: t.title, kind: late ? 'overdue' : 'due', text: late ? `Đã quá hạn từ ${dmy(t.due)}` : `Sắp đến hạn: ${dmy(t.due)}`, by_name: '', by_id: null, company_id: getCompany() });
    }
    for (const ev of (eventsQ.data ?? []).filter(e => (e.attendeeIds || []).includes(me.id) && e.date <= d && (e.endDate || e.date) >= d)) {
      const id = reminderId('ev', ev.id, d, me.id);
      if (!have.has(id)) rows.push({ id, to_user: me.id, event_id: ev.id, task_id: ev.taskId || null, title: ev.title, kind: 'event', text: `Hôm nay${ev.start ? ' lúc ' + ev.start : ''}${ev.location ? ' · ' + ev.location : ''}`, by_name: '', by_id: null, company_id: getCompany() });
    }
    if (!rows.length) return;
    void supabase().from('notifications').upsert(rows, { onConflict: 'id', ignoreDuplicates: true }).then(() => { void qc.invalidateQueries({ queryKey: ['notifications'] }); });
  }, [isLoading, tasksQ.isLoading, eventsQ.isLoading, tasksQ.data, eventsQ.data, mine, me.id, qc]);
}

/** Số hiện cạnh mục Công việc (việc tôi đang phụ trách) và Lịch (lịch hôm nay). */
export function useNavBadges(): { mine: number; today: number } {
  const me = useMe();
  const tasksQ = useTasks();
  const eventsQ = useEvents();
  const d = today();
  return {
    mine: (tasksQ.data ?? []).filter(t => t.assigneeId === me.id && !['done', 'cancel'].includes(t.status)).length,
    today: (eventsQ.data ?? []).filter(e => evVisible(e, me) && e.date <= d && (e.endDate || e.date) >= d).length,
  };
}

export function NotificationsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { mine, unread } = useNotifications();
  const tasksQ = useTasks();
  const eventsQ = useEvents();
  const cos = useCompanies();
  const switchCo = useSwitchCompany();
  const [filter, setFilter] = useState<'unread' | 'all'>('unread');
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['notifications'] }); };
  const list = filter === 'unread' ? unread : mine.slice(0, 100);

  const openItem = async (n: Notif) => {
    if (!n.read) { await supabase().from('notifications').update({ read: true }).eq('id', n.id); refresh(); }
    onClose();
    // thông báo của đơn vị khác: chuyển sang đơn vị đó rồi mở mục (dữ liệu của đơn vị mới tải lại sau khi chuyển)
    if (n.companyId && n.companyId !== getCompany() && coAllowed(me, n.companyId)) {
      switchCo(n.companyId);
      if (n.eventId) nav(`/cal?ev=${n.eventId}`); else if (n.taskId) nav(`/tasks?view=${n.taskId}`);
      return;
    }
    if (n.eventId) {
      if ((eventsQ.data ?? []).some(e => e.id === n.eventId)) nav(`/cal?ev=${n.eventId}`); else toast('Lịch này đã bị hủy.');
      return;
    }
    if (n.taskId) {
      if ((tasksQ.data ?? []).some(t => t.id === n.taskId)) nav(`/tasks?view=${n.taskId}`); else toast('Công việc này đã bị xoá.');
    }
  };
  const readAll = async () => { await supabase().from('notifications').update({ read: true }).eq('to_user', me.id).eq('read', false); refresh(); };
  const clearRead = async () => { await supabase().from('notifications').delete().eq('to_user', me.id).eq('read', true); refresh(); toast('Đã xoá các thông báo đã đọc.'); };

  return (
    <Dialog open={open} title="Thông báo" onClose={onClose}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div className="subtabs small">
          <button type="button" aria-selected={filter === 'unread'} onClick={() => setFilter('unread')}>Chưa đọc</button>
          <button type="button" aria-selected={filter === 'all'} onClick={() => setFilter('all')}>Tất cả</button>
        </div>
        <button type="button" className="btn small" style={{ marginLeft: 'auto' }} onClick={() => void readAll()}>Đánh dấu đã đọc hết</button>
        <button type="button" className="btn small" onClick={() => void clearRead()}>Xoá thông báo đã đọc</button>
      </div>
      <div className="nt-list">
        {list.map(n => (
          <button key={n.id} type="button" className={`nt-item ${n.read ? '' : 'unread'}`} onClick={() => void openItem(n)}>
            <span className="ic">{NT_IC[n.kind] || '🔔'}</span>
            <span>{cos.list.length > 1 && n.companyId && <><span className="chip" style={{ fontSize: 10 }}>{coLabel(cos.list, n.companyId)}</span> </>}<b>{n.title}</b><br />{n.text}<small>{n.byName ? n.byName + ' · ' : ''}{agoText(n.at)}</small></span>
          </button>
        ))}
        {!list.length && <div className="empty" style={{ padding: 24 }}>{filter === 'unread' ? 'Không có thông báo chưa đọc.' : 'Chưa có thông báo.'}</div>}
      </div>
    </Dialog>
  );
}
